<template>
  <div class="_rfp_mctb_carousel_01_ rfp-mctb-carousel">
    <div class="_rfp_mctb_carousel_02_ rfp-mctb-carousel-wrapper">
      <div
        class="_rfp_mctb_carousel_03_ rfp-mctb-carousel-inner rfp-mctb-carousel-slider-direction"
        :style="`
          transform: translateX(${currentOffset}px);
          transition: ${transitionStyle};
          flex-basis: ${slideWidth}px;
          visibility: ${slideWidth ? 'visible' : 'hidden'}`"
      >
        <slot />
      </div>
      <rfp-mctb-pagination
        v-if="paginationEnabled && pageCount > 0"
        v-bind="{
          goToPage,
          pageCount,
          currentPage,
          paginationPadding,
          paginationSize,
          paginationActiveColor,
          paginationColor,
        }"
      ></rfp-mctb-pagination>
    </div>
    <rfp-mctb-navigation
      v-if="navigationEnabled"
      v-bind="{ canAdvanceForward, canAdvanceBackward, advancePage }"
      :click-target-size="navigationClickTargetSize"
      :enable-rtl="enableRtl"
    ></rfp-mctb-navigation>
  </div>
</template>

<script setup>
/**
 * HACK:
 * This empty script is required to make nested components work in Vue 3
 * while keeping compatibility with Vue 2 intact.
 */
</script>

<script>
import eventBus from '@refapp/eventbus-js';
import autoplay from './mixins/rfp-mctb-autoplay';
import RfpMctbNavigation from '../rfp-mctb-slider/rfp-mctb-navigation.vue';
import RfpMctbPagination from '../rfp-mctb-slider/rfp-mctb-pagination.vue';

export default {
  name: 'rfp-mctb-carousel',
  components: {
    RfpMctbNavigation,
    RfpMctbPagination,
  },
  mixins: [autoplay],
  props: {
    easing: {
      type: String,
      default: 'ease',
    },
    minSwipeDistance: {
      type: Number,
      default: 8,
    },
    navigationClickTargetSize: {
      type: Number,
      default: 8,
    },
    navigationEnabled: {
      type: Boolean,
      default: true,
    },
    paginationActiveColor: {
      type: String,
      default: '#000000',
    },
    paginationColor: {
      type: String,
      default: '#efefef',
    },
    paginationEnabled: {
      type: Boolean,
      default: true,
    },
    paginationPadding: {
      type: Number,
      default: 10,
    },
    paginationSize: {
      type: Number,
      default: 10,
    },
    perPage: {
      type: Number,
      default: 1,
    },
    perPageCustom: {
      type: Array,
    },
    scrollPerPage: {
      type: Boolean,
      default: false,
    },
    speed: {
      type: Number,
      default: 500,
    },
    loop: {
      type: Boolean,
      default: false,
    },
    enableRtl: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      browserWidth: null,
      carouselWidth: null,
      currentPage: 0,
      dragOffset: 0,
      dragStartX: 0,
      mousedown: false,
      slideCount: 0,
    };
  },
  computed: {
    breakpointSlidesPerPage() {
      if (!this.perPageCustom) {
        return this.perPage;
      }

      const breakpointArray = this.perPageCustom;
      const width = this.browserWidth;

      const breakpoints = breakpointArray.sort((a, b) => (a[0] > b[0] ? -1 : 1));
      const matches = breakpoints.filter((breakpoint) => width >= breakpoint[0]);

      const match = matches[0] && matches[0][1];

      return match || this.perPage;
    },
    canAdvanceForward() {
      return this.loop || this.currentPage < this.pageCount - 1;
    },
    canAdvanceBackward() {
      return this.loop || this.currentPage > 0;
    },
    currentPerPage() {
      /* istanbul ignore next line */
      return !this.perPageCustom || this.$isServer ? this.perPage : this.breakpointSlidesPerPage;
    },
    currentOffset() {
      const page = this.currentPage;
      const width = this.slideWidth;
      /* istanbul ignore next line */
      const dragged = this.dragOffset;

      const offset = this.scrollPerPage ? page * width * this.currentPerPage : page * width;

      return (offset + dragged) * -1;
    },
    isHidden() {
      return this.carouselWidth <= 0;
    },

    pageCount() {
      const slideCount = this.slideCount;
      const perPage = this.currentPerPage;

      if (this.scrollPerPage) {
        const pages = Math.ceil(slideCount / perPage);
        return pages < 1 ? 1 : pages;
      }

      return slideCount - (this.currentPerPage - 1);
    },

    slideWidth() {
      const width = this.carouselWidth;
      const perPage = this.currentPerPage;

      return width / perPage;
    },
    transitionStyle() {
      /* istanbul ignore next line */
      return `${this.speed / 1000}s ${this.easing} transform`;
    },
  },
  beforeUpdate() {
    this.computeCarouselWidth();
  },
  mounted() {
    if (!this.$isServer) {
      if ('ontouchstart' in window) {
        this.$el.addEventListener('touchstart', this.handleMousedown);
        this.$el.addEventListener('touchend', this.handleMouseup);
        this.$el.addEventListener('touchmove', this.handleMousemove);
      } else {
        this.$el.addEventListener('mousedown', this.handleMousedown);
        this.$el.addEventListener('mouseup', this.handleMouseup);
        this.$el.addEventListener('mousemove', this.handleMousemove);
      }
    }

    this.attachMutationObserver();
    this.computeCarouselWidth();
  },
  destroyed() {
    if (!this.$isServer) {
      this.detachMutationObserver();
      window.removeEventListener('resize', this.getBrowserWidth);
      if ('ontouchstart' in window) {
        this.$el.removeEventListener('touchmove', this.handleMousemove);
      } else {
        this.$el.removeEventListener('mousemove', this.handleMousemove);
      }
    }
  },
  methods: {
    getNextPage() {
      if (this.currentPage < this.pageCount - 1) {
        return this.currentPage + 1;
      }
      return this.loop ? 0 : this.currentPage;
    },
    getPreviousPage() {
      if (this.currentPage > 0) {
        return this.currentPage - 1;
      }
      return this.loop ? this.pageCount - 1 : this.currentPage;
    },
    advancePage(direction) {
      if (direction && direction === 'backward' && this.canAdvanceBackward) {
        this.goToPage(this.getPreviousPage());
      } else if (
        (!direction || (direction && direction !== 'backward')) &&
        this.canAdvanceForward
      ) {
        this.goToPage(this.getNextPage());
      }
    },
    attachMutationObserver() {
      const MutationObserver =
        window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;
      if (MutationObserver) {
        const config = { attributes: true, data: true };

        this.mutationObserver = new MutationObserver(() => {
          this.$nextTick(this.computeCarouselWidth);
        });
        if (this.$parent.$el) {
          this.mutationObserver.observe(this.$parent.$el, config);
        }
      }
    },
    detachMutationObserver() {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
    },
    getBrowserWidth() {
      this.browserWidth = window.innerWidth;
      return this.browserWidth;
    },
    getCarouselWidth() {
      this.carouselWidth = (this.$el && this.$el.clientWidth) || 0;
      return this.carouselWidth;
    },
    getSlideCount() {
      const isVue3Slot = this.$slots.default instanceof Function;
      let slotContent;

      slotContent = this.$slots.default;

      if (isVue3Slot) {
        slotContent = this.$slots.default();
      }

      // We must only include slides.
      this.slideCount = slotContent.filter((slot) => slot?.tag).length;
      // this.$slots?.default().filter((slot) => slot.tag && slot.tag.indexOf('slide') > -1)
    },
    goToPage(page) {
      if (page >= 0 && page <= this.pageCount) {
        this.currentPage = page;
        this.$emit('pageChange', this.currentPage);
        eventBus.emit('rfp-global-page-change', this.currentPage);
      }
    },
    handleMousedown(e) {
      if (!e.touches) {
        e.preventDefault();
      }

      this.mousedown = true;
      this.dragStartX = 'ontouchstart' in window ? e.touches[0].clientX : e.clientX;
    },
    handleMouseup() {
      this.mousedown = false;
      this.dragOffset = 0;
    },
    handleMousemove(e) {
      if (!this.mousedown) {
        return;
      }

      const eventPosX = 'ontouchstart' in window ? e.touches[0].clientX : e.clientX;
      const deltaX = this.dragStartX - eventPosX;

      this.dragOffset = deltaX;

      if (this.dragOffset > this.minSwipeDistance) {
        this.handleMouseup();
        this.advancePage();
      } else if (this.dragOffset < -this.minSwipeDistance) {
        this.handleMouseup();
        this.advancePage('backward');
      }
    },
    computeCarouselWidth() {
      this.getSlideCount();
      this.getBrowserWidth();
      this.getCarouselWidth();
      this.setCurrentPageInBounds();
    },
    setCurrentPageInBounds() {
      if (!this.canAdvanceForward) {
        const setPage = this.pageCount - 1;
        this.currentPage = setPage >= 0 ? setPage : 0;
      }
    },
  },
};
</script>
